import { useState, useRef, useEffect, type ReactNode } from "react";
import { motion } from "framer-motion";
import { formatAccountName } from "../utils/accounts";

interface AccountSuggestion {
  account: string;
  reason?: string;
}

interface AccountSelectorProps {
  suggestions: AccountSuggestion[];
  allAccounts: string[];
  currentAccount: string;
  onSelect: (account: string) => void;
  onClose: () => void;
}

export function AccountSelector({
  suggestions,
  allAccounts,
  currentAccount,
  onSelect,
  onClose,
}: AccountSelectorProps): ReactNode {
  const [search, setSearch] = useState("");
  const [showAll, setShowAll] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (showAll && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showAll]);

  useEffect(() => {
    function handleEscape(event: KeyboardEvent): void {
      if (event.key === "Escape") onClose();
    }
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  const searchLower = search.toLowerCase();
  const filteredAccounts = search
    ? allAccounts.filter(
        (a) =>
          (a.toLowerCase().includes(searchLower) ||
            formatAccountName(a).toLowerCase().includes(searchLower)) &&
          a !== currentAccount,
      )
    : allAccounts.filter((a) => a !== currentAccount);

  // Build suggestion list — deduplicate against current account
  const displaySuggestions = suggestions.filter((s) => s.account !== currentAccount);

  return (
    <motion.div
      className="account-selector-backdrop"
      ref={backdropRef}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      onClick={(e) => {
        if (e.target === backdropRef.current) onClose();
      }}
    >
      <motion.div
        className="account-selector"
        initial={{ opacity: 0, y: 40, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 40, scale: 0.95 }}
        transition={{ type: "spring", stiffness: 300, damping: 25 }}
      >
        <div className="account-selector-header">
          <h3>Choose an account</h3>
          <button
            type="button"
            className="account-selector-close"
            onClick={onClose}
            aria-label="Close account selector"
          >
            &#10005;
          </button>
        </div>

        <div className="account-selector-current">
          <span className="account-selector-current-label">AI suggested:</span>
          <span className="account-selector-current-value">
            {formatAccountName(currentAccount)}
          </span>
        </div>

        {displaySuggestions.length > 0 && (
          <div className="account-selector-suggestions">
            <span className="account-selector-section-label">Other possibilities</span>
            {displaySuggestions.map((suggestion) => (
              <motion.button
                key={suggestion.account}
                type="button"
                className="account-selector-option"
                onClick={() => onSelect(suggestion.account)}
                whileHover={{ x: 4 }}
                whileTap={{ scale: 0.98 }}
              >
                <span className="account-selector-option-name">
                  {formatAccountName(suggestion.account)}
                </span>
                {suggestion.reason && (
                  <span className="account-selector-option-reason">{suggestion.reason}</span>
                )}
              </motion.button>
            ))}
          </div>
        )}

        <div className="account-selector-all">
          {!showAll ? (
            <button
              type="button"
              className="account-selector-show-all"
              onClick={() => setShowAll(true)}
            >
              Search all accounts
            </button>
          ) : (
            <>
              <input
                ref={inputRef}
                type="text"
                className="account-selector-search"
                placeholder="Search accounts..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoComplete="off"
              />
              <div className="account-selector-list">
                {filteredAccounts.length === 0 && (
                  <div className="account-selector-empty">No accounts match your search</div>
                )}
                {filteredAccounts.slice(0, 20).map((account) => (
                  <button
                    key={account}
                    type="button"
                    className="account-selector-option"
                    onClick={() => onSelect(account)}
                  >
                    <span className="account-selector-option-name">
                      {formatAccountName(account)}
                    </span>
                  </button>
                ))}
                {filteredAccounts.length > 20 && (
                  <div className="account-selector-more">
                    {filteredAccounts.length - 20} more — refine your search
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

export type { AccountSuggestion };
